import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, Camera, CheckCheck, ChevronDown, LogOut, Menu, Moon, Sun } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFacility } from '../context/FacilityContext';
import { useTheme } from '../context/ThemeContext';
import { MODULES } from '../constants/modules';
import Logo from './Logo';
import { getPageTitle } from '../config/routeTitles';
import { useNotifications } from '../hooks/useNotifications';

const getInitials = (nameOrUsername) => {
  const raw = (nameOrUsername || '').trim();
  if (!raw) return 'GA';

  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};


const Tooltip = ({ label, children }) => {
  // Native title provides accessibility fallback; styled tooltip is for nicer UX.
  return (
    <span className="relative inline-flex group" title={label}>
      {children}
      <span className="pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 hidden group-hover:block opacity-0 group-hover:opacity-100 transition">
        <span className="block px-2 py-1 rounded-ga-sm bg-black/80 text-white text-[10px] whitespace-nowrap">
          {label}
        </span>
      </span>
    </span>
  );
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const AppHeader = ({ onToggleSidebar, isDesktopSidebarOpen = false, showSidebarToggle = true, pendingRequestCount = 0, pendingRequests = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin, hasPermission } = useAuth();
  const {
    activeFacilityId,
    allowedFacilities,
    hasMultipleFacilities,
    setActiveFacilityId,
    activeFacility,
  } = useFacility();
  const { theme, toggleTheme } = useTheme();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);

  // General notification centre — only active for facility users (not super admin)
  const {
    notifications: systemNotifications,
    unreadCount: systemUnreadCount,
    loading: notifLoading,
    markRead,
    markAllRead,
    refresh: refreshNotifications,
  } = useNotifications({ poll: !isSuperAdmin, limit: 20 });

  // Combined unread count: system notifications + booking requests pending
  const notificationCount = isSuperAdmin
    ? 0
    : systemUnreadCount + pendingRequestCount;

  const pageTitle = useMemo(
    () => getPageTitle(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    document.title = `${pageTitle} | Groundaale`;
  }, [pageTitle]);

  useEffect(() => {
    if (!userMenuOpen && !notifOpen) return;

    const onDown = (e) => {
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (notifOpen && notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setUserMenuOpen(false);
        setNotifOpen(false);
      }
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [userMenuOpen, notifOpen]);

  const profileName = user?.name || user?.username || 'User';
  const profileRole = user?.role_name || 'Admin';
  const initials = getInitials(profileName);

  const settingsPath = isSuperAdmin ? '/superadmin/settings' : '/dashboard/settings';
  const profilePath = isSuperAdmin ? '/superadmin/settings' : '/dashboard/profile';
  const rolesPath = isSuperAdmin ? '/superadmin/roles' : '/dashboard/roles';

  const canAccessRoles = isSuperAdmin || hasPermission(MODULES.ROLES);
  const canRegisterFace = !isSuperAdmin && hasPermission(MODULES.STAFF, 'write');

  return (
    <header
      className={`ga-dashboard-header ${isDesktopSidebarOpen ? 'header--sidebar-desktop' : ''}`}
      aria-label="Groundaale header"
    >
      <div className="header-shell">
        {/* Left: menu (mobile) + logo + facility */}
        <div className="header-left">
          {showSidebarToggle && (
            <button
              type="button"
              aria-label="Toggle sidebar"
              onClick={onToggleSidebar}
              className="header-icon-btn lg:hidden"
            >
              <Menu className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
            </button>
          )}

          <div className="flex items-center gap-2 min-w-0">
            {!isDesktopSidebarOpen ? <Logo /> : null}

            {activeFacility && (
              <>
                <span className="hidden sm:inline-flex items-center px-2 py-1 rounded-ga-sm bg-ga-bg-surface-subtle/40 border border-ga-border-subtle text-[11px] text-ga-text-muted max-w-[130px] truncate">
                  {activeFacility.facility_name || 'Facility'}
                </span>

                {activeFacility.city && (
                  <span className="hidden md:inline-flex text-[11px] text-ga-text-subtle max-w-[160px] truncate">
                    {activeFacility.city}
                  </span>
                )}

                {hasMultipleFacilities && !isSuperAdmin && (
                  <select
                    aria-label="Switch facility"
                    value={activeFacilityId || ''}
                    onChange={(e) => {
                      const nextId = parseInt(e.target.value, 10);
                      setActiveFacilityId(nextId);
                    }}
                    className="hidden md:block ga-input text-xs max-w-[210px] truncate py-2 pr-8"
                  >
                    {allowedFacilities.map((f) => {
                      const statusLabel =
                        f.status === 'setup_incomplete' ? ' [Setup]' :
                        f.status === 'trial' ? ' [Trial]' :
                        f.status === 'suspended' ? ' [Suspended]' : '';
                      return (
                        <option key={f.facility_id} value={f.facility_id}>
                          {(f.facility_name || `Facility ${f.facility_id}`) + statusLabel}
                        </option>
                      );
                    })}
                  </select>
                )}
              </>
            )}
          </div>
        </div>

        <h1 className="header-title truncate" title={pageTitle}>{pageTitle}</h1>

        <div className="header-right">
          <Tooltip label={theme === 'light' ? 'Toggle Dark Mode' : 'Toggle Light Mode'}>
            <button
              type="button"
              onClick={toggleTheme}
              className="header-icon-btn"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <Moon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
              ) : (
                <Sun className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
              )}
            </button>
          </Tooltip>

          <div className="relative" ref={notifRef}>
            <Tooltip
              label={
                notificationCount > 0
                  ? `${notificationCount} new booking request(s)`
                  : 'No new notifications'
              }
            >
              <button
                type="button"
                className="header-icon-btn relative"
                aria-label={notificationCount > 0 ? `Notifications, ${notificationCount} unread` : 'Notifications'}
                onClick={() => setNotifOpen((v) => !v)}
              >
                <Bell className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 ring-2 ring-white dark:ring-gray-800">
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </span>
                )}
              </button>
            </Tooltip>

            {notifOpen && (
              <div className="absolute right-0 top-full mt-2 w-[360px] rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface shadow-ga-soft overflow-hidden z-50">
                {/* Header */}
                <div className="px-4 py-3 border-b border-ga-border-subtle flex items-center justify-between">
                  <span className="text-sm font-semibold text-ga-text-primary">Notifications</span>
                  <div className="flex items-center gap-2">
                    {notificationCount > 0 && (
                      <span className="text-[11px] font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                        {notificationCount} unread
                      </span>
                    )}
                    {systemUnreadCount > 0 && (
                      <button
                        type="button"
                        onClick={markAllRead}
                        title="Mark all system notifications as read"
                        className="text-[11px] text-ga-primary hover:underline flex items-center gap-1"
                      >
                        <CheckCheck className="h-3 w-3" />
                        All read
                      </button>
                    )}
                  </div>
                </div>

                <div className="max-h-[360px] overflow-y-auto divide-y divide-ga-border-subtle">
                  {/* Pending booking requests section */}
                  {pendingRequests.length > 0 && (
                    <>
                      <div className="px-4 py-1.5 bg-ga-bg-surface-subtle">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-ga-text-subtle">
                          Booking Requests
                        </span>
                      </div>
                      {pendingRequests.slice(0, 5).map((req) => (
                        <button
                          key={`req-${req.id}`}
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-ga-bg-surface-subtle/40 transition-colors"
                          onClick={() => {
                            setNotifOpen(false);
                            navigate('/dashboard/booking-requests');
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <span className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-amber-500" />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-ga-text-primary truncate">
                                {req.contact_name || req.team_a_captain_name || 'New request'}
                              </div>
                              <div className="text-[12px] text-ga-text-muted mt-0.5 truncate">
                                {req.service_name || req.service_type || 'Booking'} &middot;{' '}
                                {req.date ? new Date(req.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}
                              </div>
                              <div className="text-[11px] text-ga-text-subtle mt-0.5">
                                {formatTimeAgo(req.created_at)}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                      {pendingRequests.length > 5 && (
                        <button
                          type="button"
                          className="w-full text-center text-[12px] font-semibold text-ga-primary px-4 py-2 hover:underline"
                          onClick={() => { setNotifOpen(false); navigate('/dashboard/booking-requests'); }}
                        >
                          View all {pendingRequests.length} requests →
                        </button>
                      )}
                    </>
                  )}

                  {/* System notifications section */}
                  {!isSuperAdmin && (
                    <>
                      {systemNotifications.length > 0 && (
                        <div className="px-4 py-1.5 bg-ga-bg-surface-subtle">
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-ga-text-subtle">
                            System
                          </span>
                        </div>
                      )}
                      {notifLoading && systemNotifications.length === 0 ? (
                        <div className="px-4 py-6 text-center text-[12px] text-ga-text-muted">
                          Loading…
                        </div>
                      ) : systemNotifications.length > 0 ? (
                        systemNotifications.map((notif) => (
                          <button
                            key={`notif-${notif.id}`}
                            type="button"
                            className={`w-full text-left px-4 py-3 hover:bg-ga-bg-surface-subtle/40 transition-colors ${!notif.is_read ? 'bg-ga-primary-soft/20' : ''}`}
                            onClick={() => {
                              if (!notif.is_read) markRead(notif.id);
                              if (notif.link) {
                                setNotifOpen(false);
                                navigate(notif.link);
                              }
                            }}
                          >
                            <div className="flex items-start gap-3">
                              {!notif.is_read && (
                                <span className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-ga-primary" />
                              )}
                              {notif.is_read && <span className="mt-1 flex-shrink-0 w-2 h-2" />}
                              <div className="min-w-0 flex-1">
                                <div className={`text-sm truncate ${notif.is_read ? 'text-ga-text-muted font-normal' : 'text-ga-text-primary font-medium'}`}>
                                  {notif.title}
                                </div>
                                {notif.body && (
                                  <div className="text-[12px] text-ga-text-muted mt-0.5 line-clamp-2">
                                    {notif.body}
                                  </div>
                                )}
                                <div className="text-[11px] text-ga-text-subtle mt-0.5">
                                  {formatTimeAgo(notif.created_at)}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))
                      ) : null}
                    </>
                  )}

                  {/* Empty state */}
                  {pendingRequests.length === 0 && systemNotifications.length === 0 && !notifLoading && (
                    <div className="px-4 py-10 text-center text-sm text-ga-text-muted">
                      You're all caught up!
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-4 py-2.5 border-t border-ga-border-subtle flex items-center justify-between">
                  <button
                    type="button"
                    className="text-[12px] text-ga-text-muted hover:text-ga-primary"
                    onClick={() => { refreshNotifications(); }}
                  >
                    Refresh
                  </button>
                  {pendingRequests.length > 0 && (
                    <button
                      type="button"
                      className="text-[12px] font-semibold text-ga-primary hover:underline"
                      onClick={() => { setNotifOpen(false); navigate('/dashboard/booking-requests'); }}
                    >
                      All booking requests
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {user && (
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                aria-label="Open user menu"
                aria-expanded={userMenuOpen}
                onClick={() => setUserMenuOpen((v) => !v)}
                className="header-user-chip min-w-0"
              >
                <span className="header-avatar">{initials}</span>
                <div className="hidden md:flex flex-col min-w-0 items-start leading-tight text-left">
                  <span className="header-user-name truncate max-w-[150px]">{profileName}</span>
                  <span className="header-user-role truncate max-w-[150px]">{profileRole}</span>
                </div>
                <ChevronDown
                  className="h-4 w-4 shrink-0 hidden sm:block"
                  style={{ color: 'var(--ga-text-muted)' }}
                  aria-hidden
                />
              </button>

              {userMenuOpen && (
                <div className="header-dropdown w-[280px] rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface shadow-ga-soft overflow-hidden">
                    <div className="p-3 border-b border-ga-border-subtle">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-ga-full bg-ga-primary-soft border border-ga-accent/30 flex items-center justify-center">
                          <span className="text-sm font-semibold text-ga-primary">{initials}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-ga-text-primary truncate">{profileName}</div>
                          <div className="text-[11px] text-ga-text-muted truncate">{profileRole}</div>
                        </div>
                      </div>
                    </div>

                    <div className="p-2">
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 rounded-ga-sm hover:bg-white/5 text-sm text-ga-text-primary transition-colors"
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate(profilePath);
                        }}
                      >
                        Profile
                      </button>

                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 rounded-ga-sm hover:bg-white/5 text-sm text-ga-text-primary transition-colors"
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate(settingsPath);
                        }}
                      >
                        Settings
                      </button>

                      {canAccessRoles && (
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 rounded-ga-sm hover:bg-white/5 text-sm text-ga-text-primary transition-colors"
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate(rolesPath);
                          }}
                        >
                          Role/Permissions
                        </button>
                      )}

                      {canRegisterFace && (
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 rounded-ga-sm hover:bg-white/5 text-sm text-ga-text-primary transition-colors flex items-center gap-2"
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate('/dashboard/selfie-attendance/register-face');
                          }}
                        >
                          <Camera className="h-4 w-4 text-ga-text-muted" />
                          Register face
                        </button>
                      )}

                      <div className="my-1 border-t border-ga-border-subtle" />

                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 rounded-ga-sm hover:bg-white/5 text-sm text-ga-text-primary transition-colors flex items-center gap-2"
                        onClick={() => {
                          logout();
                          navigate('/login');
                        }}
                      >
                        <LogOut className="h-4 w-4 text-ga-text-muted" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
    </header>
  );
};

export default AppHeader;

